import { computed, Injectable, signal } from '@angular/core';
import { documentKey } from '../functions/document-id';
import { DeliveryNoteRow, DocumentId, InvoiceRow } from '../types/document-row';

/** Both halves of a link, so clicking either one can put the whole group on screen. */
export interface SessionLink {
  readonly invoice: InvoiceRow;
  readonly notes: readonly DeliveryNoteRow[];
}

/**
 * Links created in the current session.
 *
 * Both lists query `linked` = false, so a linked document disappears the moment the
 * grids reload. Keeping the pairs here lets the rows stay put and carry a mark until
 * the page is reloaded, and gives "Verknüpfung lösen" the invoice it has to release.
 *
 * Every id is held as its `documentKey`, because the same document reaches us as a
 * bare number from the query service and could reach us prefixed once the service
 * matches its contract — the two must never become separate entries.
 */
@Injectable()
export class InvoiceLinkSessionService {
  /** Invoice key → both halves of the link. The rows, not just their ids, so
   *  reselecting either side can put the pair back on screen without a lookup. */
  private readonly links = signal<ReadonlyMap<string, SessionLink>>(new Map());

  readonly count = computed(() => this.links().size);

  /** The pair being assembled: rows selected but not yet linked. They carry a grey
   *  mark where the red one appears once "Verknüpfen" has gone through. */
  private readonly draftKeys = signal<ReadonlySet<string>>(new Set());

  /** Every id on either side. An invoice key and a note key never collide, so one
   *  lookup serves both lists. */
  private readonly linkedKeys = computed(() => {
    const keys = new Set<string>();
    for (const [invoiceKey, link] of this.links()) {
      keys.add(invoiceKey);
      for (const note of link.notes) {
        const key = documentKey(note.goods_receipt.id);
        if (key) keys.add(key);
      }
    }
    return keys;
  });

  /** Note key → the link it belongs to. A note carries exactly one invoice, so this
   *  stays a plain lookup rather than a list. */
  private readonly linkByNoteKey = computed(() => {
    const index = new Map<string, SessionLink>();
    for (const link of this.links().values())
      for (const note of link.notes) {
        const key = documentKey(note.goods_receipt.id);
        if (key) index.set(key, link);
      }
    return index;
  });

  isLinked(id: DocumentId | null | undefined): boolean {
    const key = documentKey(id);
    return key !== null && this.linkedKeys().has(key);
  }

  /** Replaces the draft with the current selection on both sides. */
  setDraft(ids: readonly (DocumentId | null | undefined)[]): void {
    const keys = new Set<string>();
    for (const id of ids) {
      const key = documentKey(id);
      if (key !== null) keys.add(key);
    }
    this.draftKeys.set(keys);
  }

  isDraft(id: DocumentId | null | undefined): boolean {
    const key = documentKey(id);
    return key !== null && this.draftKeys().has(key);
  }

  isInvoiceLinked(invoiceId: DocumentId | null | undefined): boolean {
    const key = documentKey(invoiceId);
    return key !== null && this.links().has(key);
  }

  /** The delivery notes of a link, for putting the pair back into the selection. */
  notesOf(invoiceId: DocumentId | null | undefined): readonly DeliveryNoteRow[] {
    const key = documentKey(invoiceId);
    return (key !== null && this.links().get(key)?.notes) || [];
  }

  noteIdsOf(invoiceId: DocumentId): readonly DocumentId[] {
    return this.notesOf(invoiceId).map((note) => note.goods_receipt.id);
  }

  /** The link a delivery note belongs to, so clicking the note can mark the group. */
  linkOfNote(noteId: DocumentId | null | undefined): SessionLink | null {
    const key = documentKey(noteId);
    return (key !== null && this.linkByNoteKey().get(key)) || null;
  }

  /** The link an invoice owns, for the same reason from the other side. */
  linkOfInvoice(invoiceId: DocumentId | null | undefined): SessionLink | null {
    const key = documentKey(invoiceId);
    return (key !== null && this.links().get(key)) || null;
  }

  /** A note carries exactly one invoice, so linking it here takes it from whoever held
   *  it, and an invoice left without notes is no longer linked. The response would say
   *  as much in `detachedFromInvoiceVerificationIds`, but the service omits it — without
   *  this the previous invoice keeps a mark and a pair it no longer owns. */
  add(invoice: InvoiceRow, notes: readonly DeliveryNoteRow[]): void {
    const key = documentKey(invoice?.invoice_verification?.id);
    if (key === null) return;
    const incoming = new Set<string>();
    for (const note of notes) {
      const noteKey = documentKey(note.goods_receipt.id);
      if (noteKey !== null) incoming.add(noteKey);
    }
    const next = new Map<string, SessionLink>();
    for (const [otherKey, link] of this.links()) {
      if (otherKey === key) continue;
      const kept = link.notes.filter((note) => {
        const noteKey = documentKey(note.goods_receipt.id);
        return noteKey === null || !incoming.has(noteKey);
      });
      if (kept.length > 0) next.set(otherKey, { ...link, notes: kept });
    }
    next.set(key, { invoice, notes: [...notes] });
    this.links.set(next);
  }

  remove(invoiceId: DocumentId): void {
    const key = documentKey(invoiceId);
    if (key === null || !this.links().has(key)) return;
    const next = new Map(this.links());
    next.delete(key);
    this.links.set(next);
  }
}
